import { isElectrical, isFixedInPlace } from './objectCategoryHelper';
import { isDemoed } from './demoScope';
import { isElectricalDeviceObject } from './electricalDevices';
import { lightSymbolOf } from './lightingClassification';
import { isStagingDecor, teaTowelHasNoHandle } from '@shared-lib/staging';
import { stagingKindOf } from '@shared-lib/staging/stagingKind';

/**
 * The ONE layer-routing rule, shared by the 3D scene, the 2D plan and the
 * elevations. Each view used to carry its own copy and they drifted; anything
 * that decides whether an object belongs to a layer calls this.
 *
 * Order matters:
 *  1. Electrical devices (switches, outlets, ceiling devices) ARE the
 *     electrical drawing — they appear only in Electrical mode.
 *  2. Light fixtures are always visible in Electrical mode, whatever the
 *     Lighting layer says. An electrical drawing whose fixtures can be toggled
 *     off shows switches wired to nothing — and they are drawn there as plan
 *     symbols rather than as fixtures, so they are part of that drawing.
 *  3. Otherwise lights follow the Lighting layer (`showElectrical` — the flag
 *     kept its old name), fixed-in-place things follow Fixtures, and the
 *     leftover follows Furnishings.
 *
 * @param {Object} obj
 * @param {{showElectrical?:boolean, showFixtures?:boolean, showFurnishings?:boolean, showStaging?:boolean, electricalMode?:boolean}} flags
 * @returns {boolean}
 */
export const objectInLayer = (obj, flags = {}) => {
  if (!obj) return false;
  // The electrical-MODE branches come first and are not layer flags: a device
  // is shown by the mode or not at all, and in that mode a light shows as a
  // symbol whatever its own layer says. `layerFlagFor` deliberately does not
  // cover them — see its docstring.
  if (isElectricalDeviceObject(obj)) return !!flags.electricalMode;
  if (flags.electricalMode && lightSymbolOf(obj) !== null) return true;
  const flag = layerFlagFor(obj);
  return flag ? flags[flag] !== false : true;
};

/**
 * WHICH layer flag governs this object — the one answer, so that "is it
 * visible" and "which layer would I have to turn on to see it" can never
 * disagree.
 *
 * Returns `null` for an object no layer flag governs: an electrical DEVICE is
 * shown by `electricalMode`, which is a mode and not a layer. Revealing
 * `showElectrical` for a switch would be a silent no-op that reads as a bug.
 *
 * Staged decor gets its own layer rather than falling through to Furnishings:
 * a contractor hiding Furnishings to read the plan would otherwise lose the
 * homeowner's real scanned sofa and bed along with the dressing.
 *
 * @param {Object} obj
 * @returns {'showStaging'|'showElectrical'|'showFixtures'|'showFurnishings'|null}
 */
export const layerFlagFor = (obj) => {
  if (!obj) return null;
  if (isElectricalDeviceObject(obj)) return null; // mode, not a layer
  if (isStagingDecor(obj)) return 'showStaging';
  if (isElectrical(obj)) return 'showElectrical';
  if (isFixedInPlace(obj)) return 'showFixtures';
  return 'showFurnishings'; // leftover → furnishings
};

/**
 * Has this room been staged already?
 *
 * PROPS ONLY, deliberately. `stagingStyleId` looks like the better marker --
 * the tool writes it even when it stages NOTHING ("no clear surface or wall"),
 * so it is the only durable record that the planner has already answered -- but
 * NOTHING EVER CLEARS IT. Applying a new layout deletes the props (its
 * keep-list is `stairs` and nothing else) and leaves the style id behind, so a
 * room read through that marker is "staged" for ever while visibly bare.
 *
 * Two readers: the re-plan below, and the accessories action's label ("Add"
 * on a room with none, "Redo" / "Remove" on one that has them).
 *
 * @param {Object} room
 * @returns {boolean}
 */
export const roomHasStagedProps = (room) =>
  (room?.objects || []).some(isStagingDecor);

/**
 * What this room's non-staging contents LOOK like — identity, model and pose
 * of every object the stager does not own.
 *
 * Two readers: the before/after diff that decides whether a staged room's
 * fixtures moved, and the queue's "have we already asked about this" guard.
 * Staging objects are excluded so a re-stage cannot look like a change and
 * trigger another one.
 *
 * @param {Object} room
 * @returns {string}
 */
export const roomFixtureSignature = (room) =>
  (room?.objects || [])
    .filter((o) => !isStagingDecor(o))
    .map((o) =>
      [
        o?.id,
        o?.modelId || o?.modelUrl || '',
        o?.dimensions?.x,
        o?.dimensions?.y,
        o?.dimensions?.z,
        o?.center?.x,
        o?.center?.y,
        o?.center?.z,
        o?.rotation?.y,
      ].join(':')
    )
    .sort()
    .join('|');

/**
 * Rooms that ALREADY have accessories and whose fixtures moved: the props
 * must be re-planned. The one automatic staging path.
 *
 * RULE (the user, 2026-10-21): accessories are added ONLY by the explicit
 * "Add accessories" action (`utils/designActionItems.js`). Until then a
 * declared restyle — Fixtures & Finishes, Auto Style, a package — dressed an
 * un-staged bathroom here (the 2026-10-10 rule, `7e3fe00b6`). This never
 * names a room with no props, whatever changed in it, so neither a restyle
 * nor a new layout can add them.
 *
 * Re-planning a STAGED room is maintenance, not decoration. A restyle swaps
 * models, and a fixed-size GLB draws its OWN size whatever the object's
 * `dimensions` say, so a prop placed against the old vanity can end up inside
 * the new one. Nothing reports that: staged decor carries
 * `skipCollisionCheck` deliberately (a tray SHOULD sit on a counter), so a
 * prop buried in a resized fixture raises no pill, no highlight and no Fix
 * All. It is silent, which is why it is re-planned rather than flagged.
 *
 * Only a FIXTURE change re-plans: planning starts from scratch, so firing on
 * a finish change would move the towel every time someone repainted a wall.
 *
 * Only rooms a stager claims (`stagingKindOf`: a bathroom, or a room with a
 * kitchen in it), because `style_room_decor` refuses anything else. A room
 * with no "before" is a first load, not a change.
 *
 * @param {Array} previousRooms rooms as they were before the refresh
 * @param {Array} nextRooms     rooms after it
 * @returns {string[]} ids of staged rooms whose fixtures moved
 */
export const stagedRoomsToReplan = (previousRooms = [], nextRooms = []) => {
  const before = new Map(
    previousRooms.map((r) => [r?.id, roomFixtureSignature(r)])
  );
  return nextRooms
    .filter((room) => stagingKindOf(room) !== null)
    .filter(roomHasStagedProps)
    .filter(
      (room) =>
        before.has(room?.id) &&
        before.get(room.id) !== roomFixtureSignature(room)
    )
    .map((room) => room.id);
};

/**
 * The layers to reveal for objects a refresh ADDED, given the rooms as they
 * were before it.
 *
 * Keyed on new ids, never on the whole refreshed set. The handler this backs
 * is not only a restyle funnel -- a reprocess, a checkpoint restore and every
 * Design Center tile tap come through it too, and the unscoped ones refetch
 * the entire structure. Revealing from that would switch every layer back on
 * for someone who had just turned one off: worse than the bug it fixes.
 *
 * @param {Array} previousRooms rooms as they were before the refresh
 * @param {Array} nextObjects   every object after it
 * @returns {Object} partial flags; EMPTY when nothing was added
 */
export const revealLayersForAdded = (previousRooms = [], nextObjects = []) => {
  const previousIds = new Set(
    previousRooms.flatMap((room) => (room?.objects || []).map((o) => o?.id))
  );
  return revealLayersFor(
    nextObjects.filter((o) => o?.id && !previousIds.has(o.id))
  );
};

/**
 * The layer flags that must be ON for every one of `objects` to be visible,
 * as a partial flags object: `{showStaging: true, showFixtures: true}`.
 *
 * PURE, and it only ever sets a flag TRUE — "it never hides anything" is a
 * property of the returned object, which a test can assert directly. Feed it
 * to `updateMultipleStates`.
 *
 * Used so that a tool which ADDS objects reveals the layers they live in:
 * running Auto Style with Furnishings switched off otherwise looks like it
 * did nothing. Same rationale as the electrical placement tools, which turn
 * their mode on "so what you place is visible".
 *
 * @param {Object[]} objects
 * @returns {Object} partial flags; empty when nothing needs revealing
 */
export const revealLayersFor = (objects = []) => {
  const reveal = {};
  for (const obj of objects) {
    const flag = layerFlagFor(obj);
    if (flag) reveal[flag] = true;
  }
  return reveal;
};

/**
 * 3D-scene visibility routing. `flags` is typically the viewerState.
 *
 * @param {Object} obj
 * @param {{showElectrical?:boolean, showFixtures?:boolean, showFurnishings?:boolean, electricalMode?:boolean}} [flags]
 * @returns {boolean}
 */
export const objectLayerVisible = (obj, flags = {}) => {
  if (isDemoed(obj)) return false; // demo view: demoed objects are hidden in 3D
  return objectInLayer(obj, flags);
};

/**
 * Whether the 3D scene draws `obj`, one of `roomObjects`: its layer is on,
 * and it is not a hung tea towel whose range is gone or became a catalog
 * model (no handle to hang on: not drawn, in any view). The scene's catalog
 * loop AND its procedural loop ask this — the towel has a procedural
 * renderer, so a check in the catalog loop alone never ran for it.
 */
export const sceneObjectDrawn = (obj, roomObjects, flags = {}) =>
  objectLayerVisible(obj, flags) && !teaTowelHasNoHandle(obj, roomObjects);

/**
 * 2D floor-plan visibility routing.
 *
 * @param {Object} obj
 * @param {{showElectrical:boolean, showFixtures:boolean, showFurnishings:boolean, electricalMode?:boolean}} flags
 * @returns {boolean}
 */
export const shouldShowObjectInLayer = (obj, flags) =>
  objectInLayer(obj, flags);

/**
 * Elevations visibility routing — the same rule as an array filter. It lives
 * here, beside the predicate, rather than in objectCategoryHelper: that module
 * owns the category PRIMITIVES this rule is built from, so a copy there could
 * not import the rule (circular) and had to restate it instead.
 *
 * @param {Array} objects - Objects to filter
 * @param {boolean} showFixtures
 * @param {boolean} showFurnishings
 * @param {boolean} showElectrical - The Lighting layer
 * @param {boolean} electricalMode - The Electrical drawing is on
 * @returns {Array} The objects that belong to a visible layer
 */
export const filterObjectsByVisibility = (
  objects,
  showFixtures = true,
  showFurnishings = true,
  showElectrical = true,
  electricalMode = false,
  showStaging = true
) =>
  (objects || []).filter(
    (obj) =>
      objectInLayer(obj, {
        showFixtures,
        showFurnishings,
        showElectrical,
        electricalMode,
        showStaging,
      }) && !teaTowelHasNoHandle(obj, objects)
  );
